import {
  ESdChangeReason,
  ESymbolCategory,
  EWFAssignmentType,
  EWFGatewayType,
  EWFParamType,
  EWFStepType,
  IAction,
  ISymbol,
  IWFLane,
  IWFNodeAssignment,
  IWFParameter,
  IWFStep,
  IWorkFlow,
  IWorkFlowBaseInfo,
  IWorkFlowDto
} from "@kuika/kuika-cl-model";
import {
  IProjectsWithExternalWFResponseDto,
  IProjectsWithWFResponseDto
} from "@kuika/kuika-cl-model/src/dtos/projects-response";
import {
  EWFDueUnit,
  EWFNodeType,
  IInteractionParameter,
  IStepParameter,
  IUserInteraction,
  IWFNode,
  IWFNodes
} from "@kuika/kuika-cl-model/src/models/workflow";
import { ModdleElement } from "bpmn-js/lib/BaseModeler";
import Modeler from "bpmn-js/lib/Modeler";
import NavigatedViewer from "bpmn-js/lib/NavigatedViewer";
import ObjectHash from "object-hash";
import { isPhaseGroup } from "../../../components/design-tools/flows/moddle/happyPathPhaseInterceptor";
import { BpmnService } from "../../../services/bpmn.service";
import { isWorkflowCollabEnabled } from "../../../services/collab/collab-feature-gate";
import { notifyDesignerLiveToolIdle } from "../../../services/designer-live/designer-live";
import { DesignerPresenceReporter } from "../../../services/designer-live/designer-presence";
import { WorkFlowService } from "../../../services/workflow-service";
import { WorkflowSyncService } from "../../../services/workflow-sync.service";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { normalizeStepLaneAssignments } from "../../../shared/utility/workflow-assignment-normalization";
import {
  CanvasReadability,
  classifyCanvasRead,
  describeCanvasReadability
} from "../../../shared/utility/workflow-canvas-integrity";
import {
  IDiagramElementLike,
  selectDiagramNodesToAdopt
} from "../../../shared/utility/workflow-model-diagram-reconcile";
import {
  isBpmnModelerReady,
  noteCanvasReadable,
  recordCanvasIncident
} from "../../../shared/utility/workflow-modeler-diagnostics";
import BooleanSvg from "../../../svgs/parameter-type-icons/boolean.svg";
import DateTimeSvg from "../../../svgs/parameter-type-icons/date-time.svg";
import DecimalSvg from "../../../svgs/parameter-type-icons/decimal.svg";
import DoubleSvg from "../../../svgs/parameter-type-icons/double.svg";
import GuidSvg from "../../../svgs/parameter-type-icons/guid.svg";
import Int16Svg from "../../../svgs/parameter-type-icons/int16.svg";
import Int32Svg from "../../../svgs/parameter-type-icons/int32.svg";
import Int64Svg from "../../../svgs/parameter-type-icons/int64.svg";
import StringSvg from "../../../svgs/parameter-type-icons/string.svg";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

/**
 * WorkFlowService methods swallow axios errors and return the error object in place of the
 * response, so a failed save arrives here shaped like an AxiosError, not an AxiosResponse.
 * (Twin of the screen-provider helper of the same shape.)
 */
interface AxiosErrorLikeResult {
  response?: { status?: number };
}

const getWorkflowFailedSaveHttpStatus = (result: unknown): number | undefined => {
  const errorLike: AxiosErrorLikeResult = (result ?? {}) as AxiosErrorLikeResult;
  return errorLike.response?.status;
};

export interface IDiagramProperty {
  name: string;
  value: any;
  isValueIdToGetElement?: boolean;
}

export interface WorkflowProviderState {
  workFlowList: IWorkFlowBaseInfo[];
  selectedWf: IWorkFlow | undefined;
  selectedWfIsReadOnly: boolean | undefined;
  selectedWfNode: IWFNode | undefined;
  /** Selected Happy Path phase (a `bpmn:Group` — visual only, not a JSON node) driving the phase
   *  properties panel. Mutually exclusive with `selectedWfNode`. */
  selectedWfPhaseId: string | undefined;
  isWorkFlowWindowReload: boolean;
  isCreateWfActive: boolean;
  projectsWithWFData: IProjectsWithWFResponseDto[];
  projectsWithExternalWFData: IProjectsWithExternalWFResponseDto[];
  lastOpenWfId: string | undefined;
  /** The workflow id currently being selected/loaded (set for the duration of `setSelectedWf`), so the
   *  header can show the loading process's name before its full document lands. Undefined when idle. */
  selectingWfId: string | undefined;
  loadWorkFlowList: (projectId: string) => Promise<void>;
  saveWorkFlow: (wf: IWorkFlow, changeReason?: ESdChangeReason) => Promise<boolean>;
  setIsWorkFlowWindowReload: (value: boolean) => void;
  setSelectedWf: (workflowId: string) => Promise<void>;
  setSelectedWfReadOnly: (value: boolean) => void;
  handleLaneAssignmentChange: (lane: IWFLane, assignment: IWFNodeAssignment) => void;
  updateWorkFlowList: (wfDto: IWorkFlowDto | IWorkFlow) => void;
  setCreatingWfActive: (value: boolean) => void;
  clearSelectedWf: () => Promise<void>;
  clearLastOpenedWfId: () => void;
  clearWfNodeSelection: () => void;
  /** Rename a Happy Path phase by its `phaseId` — drives the canvas group's label so canvas and
   *  model stay in sync (the interceptor's updateLabel hook writes `happyPathDefinition`). */
  renameWfPhase: (phaseId: string, name: string) => void;
  addActionToWorkflowStep: (action: IAction, changeReason?: ESdChangeReason) => void;
  deleteActionFromWorkflowStep: (action: IAction, changeReason?: ESdChangeReason) => void;
  setWFStepParameters: (step: IWFStep, params: IWFParameter[], paramType: EWFParamType, process?: IWorkFlow) => void;
  getSelectedWfStep: () => IWFStep | undefined;
  setSelectedWfStep: (stepId: string) => void;
  updateWfStep: (step: IWFStep, process?: IWorkFlow) => void;
  setSelectedWfNodeName: (name: string) => void;
  getAssignmentTypeValue: (assignmentType: EWFAssignmentType | undefined) => string;
  elementsOverlap: (el1, el2) => boolean;
  getProjectsWithWorkflowData: () => Promise<IProjectsWithWFResponseDto[] | undefined>;
  getProjectsWithExternalWorkflowData: () => Promise<IProjectsWithExternalWFResponseDto[] | undefined>;
  getTypeIcon: (type: string) => React.ReactNode;
  setSelectedWfNodeId: (nodeId: string | undefined) => void;
  createModeler: (modelerRef: HTMLElement | null, isReadOnly: boolean) => Promise<Modeler | NavigatedViewer>;
  getModeler: () => Modeler | NavigatedViewer | undefined;
  resetModeler: () => void;
  updateDiagramElement: (elementId: string, properties: IDiagramProperty[]) => void;
  iterateAllStepsRecursively: (steps: IWFStep[], callback: (step: IWFStep) => void) => void;
  findStepRecursively: (workflow: IWorkFlow, condition: (step: IWFStep) => boolean) => IWFStep | undefined;
  findAllStepsRecursively: (workflow: IWorkFlow, condition: (step: IWFStep) => boolean) => IWFStep[];
  findNodeRecursively: (nodeId: string, nodeType: EWFNodeType, workflow: IWorkFlow) => IWFNode | undefined;
  findAllNodesRecursively: (
    workflow: IWorkFlow,
    nodeType: EWFNodeType,
    condition?: (node: IWFNode) => boolean
  ) => IWFNode[];
}

/** One entry of the workflow tab-switch cache: the loaded process DTO plus the server change-token it carried. */
interface IWfCacheEntry {
  /** The workflow's audit UpdatedDate at load time (from `WorkFlowService.GetLastModified`), or undefined if unknown. */
  token: string | undefined;
  /** The raw DTO (xml + json as strings) — cheap to hold and re-convert; `convertWFDtoToWf` never mutates it. */
  dto: IWorkFlowDto;
}

/**
 * KOA-18136 §32 — the empty node skeleton a workflow model starts from.
 *
 * Mirrors what the process window builds when it rebuilds the model from the
 * canvas. `description` and `dueIn` are the model's own required fields and carry
 * no meaning until an author sets them.
 */
export function emptyWorkflowNodes(): IWFNodes {
  return {
    description: "",
    dueIn: { dueUnit: EWFDueUnit.Days, dueValue: 0 },
    steps: [],
    forwards: [],
    decisions: [],
    gateways: [],
    events: [],
    lanes: []
  };
}

export class WorkflowProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  WorkflowProviderState
> {
  static defaultState: WorkflowProviderState = {
    workFlowList: [],
    selectedWf: undefined,
    selectedWfIsReadOnly: undefined,
    selectedWfNode: undefined,
    selectedWfPhaseId: undefined,
    isWorkFlowWindowReload: false,
    isCreateWfActive: false,
    projectsWithWFData: [],
    projectsWithExternalWFData: [],
    lastOpenWfId: undefined,
    selectingWfId: undefined,
    loadWorkFlowList: () => new Promise(() => {}),
    saveWorkFlow: () => new Promise(() => false),
    setSelectedWf: async () => {},
    setSelectedWfReadOnly: () => {},
    handleLaneAssignmentChange: () => {},
    setSelectedWfStep: () => {},
    getSelectedWfStep: () => undefined,
    updateWfStep: () => {},
    setSelectedWfNodeName: () => {},
    getAssignmentTypeValue: () => "",
    elementsOverlap: () => false,
    getProjectsWithWorkflowData: () => new Promise(() => []),
    getProjectsWithExternalWorkflowData: () => new Promise(() => []),
    getTypeIcon: (type: string) => null,
    updateWorkFlowList: () => {},
    setCreatingWfActive: () => {},
    clearSelectedWf: async () => {},
    clearLastOpenedWfId: () => {},
    addActionToWorkflowStep: () => {},
    deleteActionFromWorkflowStep: () => {},
    setWFStepParameters: () => {},
    clearWfNodeSelection: () => {},
    renameWfPhase: () => {},
    setSelectedWfNodeId: () => {},
    setIsWorkFlowWindowReload: () => {},
    createModeler: () => new Promise(() => {}),
    getModeler: () => undefined,
    resetModeler: () => {},
    updateDiagramElement: () => {},
    iterateAllStepsRecursively: () => {},
    findStepRecursively: () => undefined,
    findAllStepsRecursively: () => [],
    findNodeRecursively: () => undefined,
    findAllNodesRecursively: () => []
  };

  private bpmnService: BpmnService;

  private syncService: WorkflowSyncService;
  private reClickedSelectedElement: boolean = false;

  private pendingChanges: Map<
    string,
    { element: any; operation: "create" | "update" | "delete"; isTransformation?: boolean }[]
  > = new Map();

  private addPendingChange(
    elementId: string,
    change: { element: any; operation: "create" | "update" | "delete"; isTransformation?: boolean }
  ) {
    const existing = this.pendingChanges.get(elementId) || [];
    existing.push(change);
    this.pendingChanges.set(elementId, existing);
  }

  private clearPendingChangesForElement(elementId: string) {
    this.pendingChanges.delete(elementId);
  }

  private hasPendingChanges(elementId: string): boolean {
    return this.pendingChanges.has(elementId);
  }

  constructor(partial: ScreenDesignContextProvider) {
    super(partial);
    this.bpmnService = new BpmnService();
    this.syncService = new WorkflowSyncService();

    this.bpmnService.onBpmnEvent({
      "commandStack.changed": this.handleCommandStackChanged.bind(this),
      "commandStack.shape.create.postExecuted": this.handleShapeCreate.bind(this),
      "commandStack.shape.delete.postExecuted": this.handleShapeDelete.bind(this),
      "commandStack.shape.replace.postExecuted": this.handleShapeTypeChange.bind(this),
      "commandStack.connection.create.preExecute": this.handleConnectionCreate.bind(this),
      "commandStack.connection.create.postExecuted": this.handleConnectionCreateEnd.bind(this),
      "commandStack.connection.delete.postExecuted": this.handleConnectionDelete.bind(this),
      "commandStack.connection.layout.postExecuted": this.handleConnectionUpdate.bind(this),
      "shape.move.end": this.handleShapeMoveEnd.bind(this),
      "commandStack.lane.add.postExecuted": this.handleLaneAdd.bind(this),
      "element.click": this.handleElementClick.bind(this),
      "selection.changed": this.handleSelectionChanged.bind(this),
      "commandStack.element.updateLabel.postExecuted": this.handleElementUpdateLabel.bind(this)
    });
  }

  getProviderValues = (): WorkflowProviderState => {
    return {
      workFlowList: this.state.workFlowList,
      selectedWf: this.state.selectedWf,
      selectedWfIsReadOnly: this.state.selectedWfIsReadOnly,
      selectedWfNode: this.state.selectedWfNode,
      selectedWfPhaseId: this.state.selectedWfPhaseId,
      isWorkFlowWindowReload: this.state.isWorkFlowWindowReload,
      isCreateWfActive: this.state.isCreateWfActive,
      projectsWithWFData: this.state.projectsWithWFData,
      projectsWithExternalWFData: this.state.projectsWithExternalWFData,
      lastOpenWfId: this.state.lastOpenWfId,
      selectingWfId: this.state.selectingWfId,
      loadWorkFlowList: this.loadWorkFlowList,
      saveWorkFlow: this.saveWorkFlow,
      setIsWorkFlowWindowReload: this.setIsWorkFlowWindowReload,
      setSelectedWf: this.setSelectedWf,
      setSelectedWfReadOnly: this.setSelectedWfReadOnly,
      handleLaneAssignmentChange: this.handleLaneAssignmentChange,
      setSelectedWfStep: this.setSelectedWfStep,
      getSelectedWfStep: this.getSelectedWfStep,
      updateWfStep: this.updateWfStep,
      setSelectedWfNodeName: this.setSelectedWfNodeName,
      getAssignmentTypeValue: this.getAssignmentTypeValue,
      elementsOverlap: this.elementsOverlap,
      getProjectsWithWorkflowData: this.getProjectsWithWorkflowData,
      getProjectsWithExternalWorkflowData: this.getProjectsWithExternalWorkflowData,
      getTypeIcon: this.getTypeIcon,
      updateWorkFlowList: this.updateWorkFlowList,
      setCreatingWfActive: this.setCreatingWfActive,
      clearSelectedWf: this.clearSelectedWf,
      clearLastOpenedWfId: this.clearLastOpenedWfId,
      addActionToWorkflowStep: this.addActionToWorkflowStep,
      deleteActionFromWorkflowStep: this.deleteActionFromWorkflowStep,
      setWFStepParameters: this.setWFStepParameters,
      clearWfNodeSelection: this.clearWfNodeSelection,
      renameWfPhase: this.renameWfPhase,
      setSelectedWfNodeId: this.setSelectedWfNodeId,
      createModeler: this.createModeler,
      getModeler: this.getModeler,
      resetModeler: this.resetModeler,
      updateDiagramElement: this.updateDiagramElement,
      iterateAllStepsRecursively: this.iterateAllStepsRecursively,
      findStepRecursively: this.findStepRecursively,
      findAllStepsRecursively: this.findAllStepsRecursively,
      findNodeRecursively: this.findNodeRecursively,
      findAllNodesRecursively: this.findAllNodesRecursively
    };
  };

  getTypeIcon = (type: string) => {
    switch (type) {
      case "Unknown":
        return <StringSvg />;
      case "Boolean":
        return <BooleanSvg />;
      case "Bytea":
        return <DoubleSvg />;
      case "Byte":
        return <DoubleSvg />;
      case "DateTimeOffset":
        return <DateTimeSvg />;
      case "Decimal":
        return <DecimalSvg />;
      case "Double":
        return <DoubleSvg />;
      case "Guid":
        return <GuidSvg />;
      case "Int16":
        return <Int16Svg />;
      case "Int32":
        return <Int32Svg />;
      case "Int64":
        return <Int64Svg />;
      case "Single":
        return <DoubleSvg />;
      case "String":
        return <StringSvg />;
      // case "Image":
      //   return <ImageSvg />;
      default:
        return <StringSvg />;
    }
  };

  updateDiagramElement = (elementId: string, properties: IDiagramProperty[]) => {
    const element = this.bpmnService.getElementById(elementId);
    if (element) {
      let propertiesToUpdate: any = {};
      properties.forEach((property) => {
        if (property.isValueIdToGetElement) {
          const valueElement = this.bpmnService.getElementById(property.value);
          if (valueElement) {
            propertiesToUpdate[property.name] = valueElement;
          }
        } else {
          propertiesToUpdate[property.name] = property.value;
        }
      });
      this.bpmnService.updateElementProperties(elementId, propertiesToUpdate);
    }
  };

  // #region BPMN Service Events

  handleCommandStackChanged = (event: any) => {
    // Remote collab materialization drives the same command stack — those commands are
    // NOT local edits: drop their pending entries and never re-diff them as local ops.
    if (this.partial.workflowCollabProvider.isApplyingRemoteWorkflowChange()) {
      this.pendingChanges.clear();
      return;
    }
    // All commands in the batch are completed, now sync everything
    this.processPendingChanges();
    // Geometry-only commands (plain moves/resizes) leave pendingChanges empty and skip the
    // save funnel — in a live session they still must replicate, so capture them here.
    if (this.partial.workflowCollabProvider.isWorkflowSessionActive()) {
      this.partial.workflowCollabProvider.commitWorkflowLocalDoc();
    }
  };

  handleShapeCreate = (event: any) => {
    const element = event.context?.shape;
    if (!element) return;

    if (event.context?.hints && !event.context?.hints?.hasOwnProperty("targetElement"))
      this.bpmnService.SetUniqueLabel(event.context);

    // Store the change for later processing
    this.addPendingChange(element.id, {
      element,
      operation: "create"
    });
  };

  handleShapeDelete = (event: any) => {
    const element = event.context?.shape;
    if (!element) return;

    // Store the change for later processing
    this.addPendingChange(element.id, {
      element,
      operation: "delete"
    });
  };

  handleShapeTypeChange = (event: any) => {
    const oldElement = event.context?.oldShape;
    const newElement = event.context?.newShape;
    if (!oldElement || !newElement) return;

    const oldElementId = oldElement.id;
    const newElementId = newElement.id;
    const newElementDiId = newElement.di?.id.slice(0, -3);

    const oldElementType = oldElement.type;
    const newElementType = newElement.type;

    // Special handling for adaptive timer event transformations (same ID, different type)
    const isAdaptiveTimerTransformation =
      oldElementId === newElementId &&
      ((oldElementType === "bpmn:IntermediateCatchEvent" && newElementType === "bpmn:BoundaryEvent") ||
        (oldElementType === "bpmn:BoundaryEvent" && newElementType === "bpmn:IntermediateCatchEvent"));

    if (isAdaptiveTimerTransformation) {
      // For adaptive timer events, only add a create operation with transformation flag
      this.addPendingChange(newElementId, {
        element: newElement,
        operation: "create",
        isTransformation: true
      });
    } else {
      // Standard shape replacement logic
      if (
        oldElementId !== newElementId &&
        this.hasPendingChanges(newElementId) &&
        !(oldElementType == "bpmn:IntermediateCatchEvent" && newElementType == "bpmn:BoundaryEvent")
      ) {
        this.clearPendingChangesForElement(newElementId);
      }

      if (newElementDiId && this.hasPendingChanges(newElementDiId)) {
        this.clearPendingChangesForElement(newElementDiId);
      }

      this.addPendingChange(oldElementId, {
        element: oldElement,
        operation: "delete"
      });
      this.addPendingChange(newElementId, {
        element: newElement,
        operation: "create"
      });
    }
  };

  handleConnectionCreate = (event: any) => {
    const element = event.context?.connection;
    if (!element) return;

    const parent = event.context?.source;
    if (parent && (parent.type === "bpmn:ExclusiveGateway" || parent.type === "bpmn:InclusiveGateway")) {
      let i = 1;
      if (parent.businessObject && parent.businessObject.outgoing) {
        while (parent.businessObject.outgoing.some((element) => element.name === `Path ${i}`.trim())) {
          i++;
        }
      }
      event.context!.connection!.businessObject.name = `Path ${i}`;
    }
  };

  handleConnectionCreateEnd = (event: any) => {
    const element = event.context?.connection;
    if (!element) return;

    // Store the change for later processing
    this.addPendingChange(element.id, {
      element,
      operation: "create"
    });
  };

  handleConnectionDelete = (event: any) => {
    const element = event.context?.connection;
    if (!element) return;

    // Store the change for later processing
    this.addPendingChange(element.id, {
      element,
      operation: "delete"
    });
  };

  handleConnectionUpdate = (event: any) => {
    const element = event.context?.connection;
    if (!element) return;

    this.addPendingChange(element.id, {
      element,
      operation: "update"
    });
  };

  handleShapeMoveEnd = (event: any) => {
    const element = event.context?.shape;
    if (!element) return;

    if (element.type !== "bpmn:SubProcess" && element.type !== "bpmn:UserTask") {
      return;
    }

    if (element.parent?.type !== "bpmn:Participant") {
      return;
    }

    const workflow = this.state.selectedWf;
    if (!workflow) return;

    const delta = event.context?.delta;
    if (!delta) return;

    if (this.bpmnService.didElementChangeLane(element, delta)) {
      if (element.type === "bpmn:UserTask") {
        const step = this.findStepRecursively(workflow, (s) => s.id === element.id);
        if (step?.useLaneAssignment) {
          const diagramLane = this.bpmnService.findContainingLane(element);
          const workflowLane = workflow.nodes?.lanes?.find((l) => l.id === diagramLane?.id);
          step.assignment = workflowLane?.assignment!;
        }
      } else if (element.type === "bpmn:SubProcess") {
        // TODO: update any user task in subprocess (or nested tasks if any inner subprocess exists) according to the new lane if needed
      }

      this.saveWorkFlow(workflow, ESdChangeReason.WorkflowListUpdate);
    }
  };

  handleElementUpdateLabel = (event: any) => {
    const element = event.context?.element;
    if (!element) return;

    let labelShouldBeUnique = false;
    if (element.type === "bpmn:Task" || element.type === "bpmn:UserTask" || element.type === "bpmn:CallActivity") {
      labelShouldBeUnique = true;
    }
    // Check if the new label is unique
    if (event.context?.newLabel) {
      if (labelShouldBeUnique) {
        this.bpmnService.SetUniqueLabel(event.context);
      }

      // Store the change for later processing
      this.addPendingChange(element.id, {
        element,
        operation: "update"
      });
    }
  };

  /**
   * Process all pending changes when command stack is complete
   */
  private processPendingChanges() {
    if (this.pendingChanges.size === 0) return;

    const workflow = this.state.selectedWf;
    if (!workflow) return;

    // Process all changes
    this.pendingChanges.forEach((changes) => {
      changes.forEach((change) => {
        const targetWorkflow = this.determineTargetWorkflow(change.element, workflow);

        switch (change.operation) {
          case "create":
            this.syncService.syncElement(change.element, workflow, targetWorkflow);
            break;
          case "update":
            this.syncService.syncElement(change.element, workflow, targetWorkflow);
            break;
          case "delete":
            // Skip delete operations for transformations
            if (!change.isTransformation) {
              this.syncService.deleteElement(change.element, workflow, targetWorkflow);
            }
            break;
        }
      });
    });

    // Clear pending changes and save
    this.pendingChanges.clear();
    this.saveWorkFlow(workflow, ESdChangeReason.WorkflowListUpdate);
  }

  /**
   * Determines the target workflow context for an element
   * Traverses the element hierarchy to find the correct subprocess workflow
   */
  private determineTargetWorkflow(element: any, workflow: IWorkFlow): any {
    if (!element || !workflow || !workflow.nodes) {
      return workflow?.nodes;
    }

    // Start with the root workflow
    let currentWorkflow = workflow.nodes;
    let currentElement = element;
    let hierarchyPath = [element.id];

    // Traverse up the parent chain
    while (currentElement && currentElement.parent) {
      const parent = currentElement.parent.businessObject;
      hierarchyPath.push(parent.id);

      // If parent is a subprocess, we need to find it in the current workflow
      if (parent.$type === "bpmn:SubProcess") {
        // Look for the subprocess step in the current workflow
        const subprocessStep = currentWorkflow.steps?.find((s) => s.id === parent.id);

        if (subprocessStep && subprocessStep.subprocess) {
          // Move to the subprocess workflow
          currentWorkflow = subprocessStep.subprocess;
          currentElement = parent;
        } else {
          console.log(
            `[WorkflowProvider] Subprocess step ${parent.id} not found in current workflow, staying at current level`
          );
          // Subprocess step doesn't exist yet in the workflow model
          // This means the element should be synced to the current workflow level
          // (either root or the current subprocess we're in)
          break;
        }
      } else {
        currentElement = parent;
      }
    }
    return currentWorkflow;
  }

  handleLaneAdd = (event: any) => {
    const element = event.context?.newLane;
    if (!element) return;
    this.bpmnService.updateElementProperties(element.id, { name: "Everyone" });
  };

  handleElementClick = (event: any) => {
    const element = event.element;
    if (!element || element.type === "bpmn:Process" || element.type === "bpmn:Collaboration") return;
    const selection = this.bpmnService.getModeler()?.get("selection") as any;
    const currentSelection = selection?.get() || [];
    if (currentSelection.length === 1 && currentSelection[0] === element) {
      this.reClickedSelectedElement = true;
    }
  };

  handleSelectionChanged = (event: any) => {
    // Live session presence: selection changes broadcast (throttled) to the roster.
    this.partial.workflowCollabProvider.broadcastWorkflowSelection();
    if (event.newSelection && event.newSelection.length === 1) {
      const element = event.newSelection[0];
      // A Happy Path phase is a bpmn:Group (visual only, no JSON node) — resolve either the group
      // itself or a selected external label pointing at one, and drive the phase panel.
      const phaseElement = isPhaseGroup(element) ? element : element?.labelTarget;
      if (isPhaseGroup(phaseElement)) {
        const phaseId: string | undefined = phaseElement.businessObject?.phaseId;
        this.setState({ selectedWfNode: undefined, selectedWfPhaseId: phaseId });
        return;
      }
      const selectedNode = this.findSelectedNode(element.id);
      if (selectedNode) {
        this.setState({ selectedWfNode: selectedNode, selectedWfPhaseId: undefined });
      } else {
        // Unrecognized selection (pool, label, off-model element): fall back to process
        // properties rather than leaving the previous node's panel stale (KOA-15840).
        this.clearWfNodeSelection();
      }
    } else if (this.reClickedSelectedElement && event.newSelection?.length === 0 && event.oldSelection?.length === 1) {
      // User re-clicked the already-selected node — re-select it instead of clearing
      this.reClickedSelectedElement = false;
      const element = event.oldSelection[0];
      const selection = this.bpmnService.getModeler()?.get("selection") as any;
      selection?.select(element);
    } else {
      this.clearWfNodeSelection();
    }
  };
  // #endregion

  handleLaneAssignmentChange = async (lane: IWFLane, assignment: IWFNodeAssignment) => {
    const selectedWf = this.state.selectedWf;
    if (!selectedWf) return;
    const wlane = selectedWf.nodes?.lanes?.find((l) => l.id === lane.id);
    if (!wlane) return;
    lane.assignment = assignment;

    // Use bpmnService to get elements in lane
    const elements = this.bpmnService.findElementsInLane(lane.id);

    // Update workflow state
    elements.forEach((element) => {
      if (element.type === "bpmn:UserTask") {
        const step = this.findStepRecursively(selectedWf, (s) => s.id === element.id);
        if (step?.useLaneAssignment) {
          step.assignment = { ...assignment };
        }
      } else if (element.type === "bpmn:SubProcess") {
        this.updateSubprocessAssignments(element.Id, assignment);
      }
      if (element.type === "bpmn:Task") {
        const step = this.findStepRecursively(selectedWf, (s) => s.id === element.id);
        if (step?.useLaneAssignment) {
          step.assignment = {
            assignmentType: EWFAssignmentType.none,
            assignmentId: "None",
            dynamicAssignmentAction: undefined,
            dynamicAssignmentActionField: undefined,
            roleAssignmentParameter: undefined
          };
        }
      }
    });

    let laneName = "Everyone";
    switch (assignment.assignmentId) {
      case "Role":
        laneName = assignment.roleAssignmentParameter?.name ?? "Role";
        break;
      case "Dynamic":
        const actionDefinition = this.partial.projectProvider.state.actionDefinitions.find(
          (ad) => ad.id === assignment.dynamicAssignmentAction?.actionDefinitionId
        );
        laneName = actionDefinition?.name ?? "Dynamic";
        break;
      case "External":
        laneName = "External";
        break;
      default:
        break;
    }

    this.bpmnService.updateElementProperties(lane.id, { name: laneName });

    // Save the workflow with updated assignments
    await this.saveWorkFlow(selectedWf, ESdChangeReason.WFStepMappingChange);
  };

  updateWorkFlowList = (wfDto: IWorkFlowDto | IWorkFlow) => {
    const { workFlowList, selectedWf } = this.state;
    let wf = "json" in wfDto ? this.convertWFDtoToWf(wfDto) : (wfDto as IWorkFlow);

    const workFlowIndex = workFlowList.findIndex((wf) => wf.id === wfDto.id);
    if (workFlowIndex !== undefined && workFlowIndex > -1) {
      const wfToUpdate = workFlowList[workFlowIndex];
      wfToUpdate.parameters = wf.parameters;
      workFlowList[workFlowIndex] = wfToUpdate;
      if (selectedWf && selectedWf.id === wfDto.id) {
        this.setState({ workFlowList, selectedWf: wf });
      } else {
        this.setState({ workFlowList });
      }
    }
  };

  /**
   * Select a workflow BY ID and commit it to state.
   *
   * AWAITABLE ON PURPOSE (KOA-17362). This used to be a fire-and-forget
   * `UpdateLock().then(() => Get().then(...))` chain that returned `void`, so
   * `await setSelectedWf(...)` resolved IMMEDIATELY — before the selection was
   * committed. Every programmatic caller (chat-driven create_workflow,
   * openWorkflowForEditing, navigateToWorkflowView) relied on that await to
   * order "select, THEN mount the BPMN canvas"; the ordering silently never
   * held. Worse, a rejected/failed `Get` fell into `convertWFDtoToWf(undefined)`
   * and threw INSIDE the dangling `.then`, so `setState` never ran, `selectedWf`
   * stayed undefined forever, `ensureModelerBuilt` could never build, and every
   * `ui_workflow_dsl` graph command failed "BPMN Modeler is not ready" — with no
   * error anywhere, because the throw was an unhandled rejection. Awaiting and
   * validating here is what makes the mount ordering real.
   */
  // Dedupe concurrent selects of the SAME workflow. Two entry paths can race to restore the last
  // process (a programmatic navigation AND the WorkFlowWindow mount that self-heals a bare toggle);
  // a duplicate fetch + advisory-lock round-trip is wasteful and, under collab, risks a double join.
  private selectInFlight?: { id: string; promise: Promise<void> };

  // Tab-switch cache: the last full workflow we loaded, keyed by id, with the server change-token it
  // had at load time. Lets a re-select (e.g. returning to the process tool) skip the heavy Xml/Json
  // fetch + parse when the process is UNCHANGED — see resolveWorkflowForSelect. Copies are isolated
  // (structuredClone) so live editing can't corrupt the cache and a stale cache can't leak into a
  // live selection.
  private wfCache: Map<string, IWfCacheEntry> = new Map();

  setSelectedWf = async (workflowId: string): Promise<void> => {
    if (this.selectInFlight?.id === workflowId) {
      return this.selectInFlight.promise;
    }
    const promise: Promise<void> = this.runSetSelectedWf(workflowId);
    this.selectInFlight = { id: workflowId, promise };
    // Publish the loading id so the header can show THIS process's name (from the list) during the
    // load — including the very first selection, when lastOpenWfId hasn't been set yet.
    this.setState({ selectingWfId: workflowId });
    try {
      await promise;
    } finally {
      // Clear only if we're still the in-flight selection (a newer select for a different id may have
      // superseded us and owns the flag now).
      if (this.selectInFlight?.id === workflowId) {
        this.selectInFlight = undefined;
        this.setState({ selectingWfId: undefined });
      }
    }
  };

  private runSetSelectedWf = async (workflowId: string): Promise<void> => {
    // Guard the id SHAPE, not just emptiness: callers historically passed the
    // whole DTO, which stringifies to "[object Object]" and silently 404s.
    if (typeof workflowId !== "string" || workflowId.trim().length === 0) {
      console.error("[workflow] setSelectedWf requires a workflow id string, got:", workflowId);
      return;
    }
    const collabEnabled = isWorkflowCollabEnabled();
    if (!collabEnabled) {
      // Collab mode skips this: the advisory lock is RETIRED there — everyone edits,
      // convergence comes from the op stream.
      await WorkFlowService.UpdateLock(workflowId);
    }
    const selectedWf: IWorkFlow | undefined = await this.resolveWorkflowForSelect(workflowId);
    // A failed fetch leaves the previous selection untouched rather than committing a
    // half-converted workflow (resolveWorkflowForSelect logs and returns undefined).
    if (!selectedWf) {
      return;
    }
    if (typeof window !== "undefined") {
      (window as any).__wfSelected = selectedWf;
    }
    await new Promise<void>((resolve) => {
      this.setState({ lastOpenWfId: workflowId, selectedWf }, () => {
        if (collabEnabled) {
          // Join happens after the workflow (and its canvas xml) is loaded; the
          // window's importXML completes before the modeler-dependent join seed is built
          // because joinWorkflowCollabSession awaits getXmlFromModeler on the live modeler.
          void this.partial.workflowCollabProvider.joinWorkflowCollabSession(workflowId);
        }
        resolve();
      });
    });
    // Presence only — the BPMN editor has its own pessimistic lock on the legacy path,
    // so no staleKind.
    DesignerPresenceReporter.reportEntity("workflow", workflowId, selectedWf?.name);
  };

  /**
   * Resolve the workflow to select, reusing the tab-switch cache when the server says it hasn't
   * changed. Reads the lightweight change-token FIRST (before any full fetch) so the cached token can
   * never be newer than its data — a match therefore proves the cached copy is still current and we
   * skip the heavy Xml/Json Get + parse. On any mismatch, a missing token (old backend / error), or a
   * cold cache we do the full fetch and refresh both the workflow list and the cache. The token also
   * covers our OWN edits for free: a save bumps the server UpdatedDate, so the next re-select misses
   * the cache and re-fetches. Returns undefined (selection unchanged) when the full fetch fails.
   */
  private resolveWorkflowForSelect = async (workflowId: string): Promise<IWorkFlow | undefined> => {
    const currentToken: string | undefined = await WorkFlowService.GetLastModified(workflowId);
    const cached: IWfCacheEntry | undefined = this.wfCache.get(workflowId);
    if (cached && currentToken !== undefined && cached.token === currentToken) {
      // Unchanged since we cached it — reuse the DTO and skip the heavy Xml/Json Get. Re-convert (a
      // fresh object each time; convertWFDtoToWf never mutates the DTO) so the cache stays isolated
      // from live editing without paying a structuredClone.
      return this.convertWFDtoToWf(cached.dto);
    }

    const res = await WorkFlowService.Get(workflowId);
    // `Get` swallows transport errors and returns the error object, so a failed fetch arrives WITHOUT
    // `data.id`. Keep any usable cache entry but don't commit a half-converted workflow.
    if (!res?.data?.id) {
      console.error(`[workflow] setSelectedWf: workflow "${workflowId}" could not be loaded — selection unchanged.`);
      return undefined;
    }
    this.updateWorkFlowList(res.data);
    this.wfCache.set(workflowId, { token: currentToken, dto: res.data });
    return this.convertWFDtoToWf(res.data);
  };

  setSelectedWfReadOnly = (isReadOnly: boolean) => {
    // Called on EVERY modeler build attempt, and each partial setState stamps a fresh
    // changeReasonParameter that PureSdComponent treats as an unconditional re-render.
    // Unguarded, a canvas that cannot build pumps its own retry loop: rebuild → setState
    // → re-render → rebuild. Writing only on a real change breaks that cycle.
    if (this.state.selectedWfIsReadOnly === isReadOnly) return;
    this.setState({ selectedWfIsReadOnly: isReadOnly });
  };

  clearLastOpenedWfId = () => {
    this.setState({ lastOpenWfId: undefined });
  };

  /**
   * Release the lock and drop the current selection.
   *
   * AWAITED, not fired-and-forgotten (KOA-17362): the un-awaited version let the
   * "release ALL my locks" call land AFTER a subsequent `setSelectedWf` had taken
   * a fresh lock, so a select-right-after-clear (create_workflow does exactly
   * that) could end up unlocked and the canvas read-only.
   */
  clearSelectedWf = async (): Promise<void> => {
    if (isWorkflowCollabEnabled()) {
      // Collab mode: no advisory lock to release — leaving the session (which also clears
      // the stale wfhash ghost) replaces the UpdateLock() release.
      this.partial.workflowCollabProvider.leaveWorkflowCollabSession();
    } else {
      await WorkFlowService.UpdateLock();
    }
    DesignerPresenceReporter.reportEntity("workflow", undefined, undefined);
    // Drop the window mirror together with the state (KOA-18136). `setSelectedWf` stamps
    // `window.__wfSelected` but nothing ever cleared it, so it outlived the selection —
    // and `describeModelerAbsence` reads `selectedWf ?? __wfSelected`. A stale stamp made
    // that diagnostic permanently blind to its single most important verdict ("no workflow
    // is selected — retrying will NOT help"), so `ui_workflow_dsl` reported a generic
    // mount race and burned the turn on identical retries instead of naming the cause.
    if (typeof window !== "undefined") {
      (window as any).__wfSelected = undefined;
    }
    await new Promise<void>((resolve) => {
      this.setState(
        {
          selectedWf: undefined,
          selectedWfNode: undefined,
          selectedWfPhaseId: undefined
        },
        () => {
          notifyDesignerLiveToolIdle();
          resolve();
        }
      );
    });
    this.bpmnService.resetModeler();
  };

  clearWfNodeSelection = () => {
    this.setState({
      selectedWfNode: undefined,
      selectedWfPhaseId: undefined
    });
  };

  renameWfPhase = (phaseId: string, name: string): void => {
    const trimmed: string = name.trim();
    if (!phaseId || !trimmed) return;
    // Rename through the canvas group's label so the HappyPathPhaseInterceptor's updateLabel hook
    // re-centres the header and writes `happyPathDefinition` — a selected phase always has a group.
    this.bpmnService.renamePhaseGroup(phaseId, trimmed);
  };

  loadWorkFlowList = async (projectId) => {
    try {
      if (!projectId) return;

      const res = await WorkFlowService.GetAllByProjectId(projectId);
      if (res?.status !== 200) return;

      const workFlowList = this.convertWFListDtoToWfList(res.data);

      // Process workflows that need migration/updates
      const updatePromises = workFlowList.map(async (wf) => {
        // VTO: WFList does not contain nodes and xml, so we ignore this part. details will be loaded when needed.
        // if (!wf.nodes || !wf.xml) {
        //   const detailRes = await WorkFlowService.Get(wf.id);
        //   if (detailRes?.status !== 200) return wf;
        //   const updatedWf = this.convertWFDtoToWf(detailRes.data);
        //   this.processWorkflowSteps(updatedWf);
        //   await this.saveWorkFlow(updatedWf);
        //   return updatedWf;
        // }
        // this.processWorkflowSteps(wf);
        // await this.saveWorkFlow(wf);
        // return wf;
      });

      await Promise.all(updatePromises);
      this.setState({ workFlowList });
      const { selectedWf } = this.state;
      if (selectedWf) {
        const wfInList = workFlowList.find((wf) => wf.id === selectedWf.id);
        if (wfInList && wfInList.name !== selectedWf.name) {
          selectedWf.name = wfInList.name;

          const elements = this.bpmnService.getAllElements({ type: "bpmn:Participant" });
          const pool = elements[0];
          if (pool?.id) {
            this.bpmnService.updateElementProperties(pool.id, { name: selectedWf?.name });
          }
          this.setState({ selectedWf });
        }
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  processWorkflowSteps = (wf: IWorkFlow) => {
    if (!wf.nodes?.steps) return;

    wf.nodes.steps.forEach((step) => {
      // Initialize output parameters if undefined
      if (step.outputParameters === undefined) {
        step.outputParameters = [];
      }

      // Migrate outputsMap to outputParameters
      if (step.outputsMap) {
        step.outputsMap.forEach((output) => {
          if (!step.outputParameters.some((p) => p.id === output.key)) {
            step.outputParameters.push({ id: output.key } as IStepParameter);
          }
        });
        step.outputsMap = undefined;
      }

      // Clear unused fields
      step.inputsMap = undefined;
      step.parameters = [];

      // Migrate mailInteractions to userInteractions
      this.migrateMailInteractionsToUserInteractions(step, wf);
    });
  };

  migrateMailInteractionsToUserInteractions = (step: IWFStep, wf: IWorkFlow) => {
    if (!step.mailInteractions?.some((x) => x.id && x.label)) return;

    if (step.userInteractions === undefined) {
      step.userInteractions = [];
    }

    const wfParameters =
      wf.parameters
        ?.filter((p) => p.isBusinessObject === false)
        ?.filter((p) => step.outputParameters.map((o) => o.id).includes(p.id)) ?? [];

    step.mailInteractions = step.mailInteractions.filter((x) => x.id && x.label);

    step.mailInteractions.forEach((mailInteraction) => {
      const userInteraction: IUserInteraction = {
        id: mailInteraction.id!,
        label: mailInteraction.label!,
        parameters: []
      };

      wfParameters.forEach((p) => {
        const mailIntParameter = mailInteraction.parameters?.find((x) => x.id === p.id);

        const newParam: IInteractionParameter = {
          id: p.id,
          isMailParameter: true,
          value: {
            ...p.defaultValue,
            label: "Fixed Value",
            category: ESymbolCategory.fixed,
            fixedValue: mailIntParameter?.value.fixedValue ?? this._getDefaultFixedValue(p.dataType)
          } as ISymbol
        };

        userInteraction.parameters.push(newParam);
      });

      step.userInteractions!.push(userInteraction);

      // Clear fields from mailInteraction
      mailInteraction.parameters = undefined;
      mailInteraction.userInteractionId = userInteraction.id;
      mailInteraction.id = undefined;
      mailInteraction.label = undefined;
    });
  };

  convertWFListDtoToWfList = (value: IWorkFlowDto[]): IWorkFlow[] => {
    const result: IWorkFlow[] = [];
    if (value) {
      value.forEach((wfDto) => {
        const wf = this.convertWFDtoToWf(wfDto);
        result.push(wf);
      });
    }
    return result;
  };

  convertWFDtoToWf = (value: IWorkFlowDto): IWorkFlow => {
    const result: IWorkFlow = {
      id: value.id,
      name: value.name,
      displayName: value.displayName,
      description: value.description,
      kProject_Id: value.kProject_Id,
      inactived: value.inactived,
      userInUse: value.userInUse
    };
    if (value.xml) {
      result.xml = value.xml;
    }
    if (value.json) {
      const json = JSON.parse(value.json);
      result.parameters = json.parameters;
      result.nodes = json.nodes;
      result.happyPathDefinition = json.happyPathDefinition;
      // Read-repair `useLaneAssignment` so steps with a step-level assignment whose flag was
      // never persisted (deserialized as undefined) don't get their Dynamic/Role panel hidden.
      normalizeStepLaneAssignments(result.nodes);
      // VTO: JSON fix
      // Normalize isBusinessObject: older saved workflows omit this flag on internal
      // parameters, leaving it undefined. Downstream UI filters use strict `=== false`
      // / `=== true`, which silently drop undefined params. A parameter is internal
      // unless explicitly marked as a business object, so undefined/null collapses to false.
      result.parameters = json.parameters?.map((p: IWFParameter): IWFParameter => ({
        ...p,
        isInput: p.parameterType === EWFParamType.Input,
        isBusinessObject: p.isBusinessObject === true
      }));
    }
    return result;
  };

  convertWFToWfDto = (value: IWorkFlow): IWorkFlowDto | undefined => {
    if (value && value.xml) {
      const json = JSON.stringify({
        parameters: value.parameters,
        nodes: value.nodes,
        happyPathDefinition: value.happyPathDefinition
      });
      const result: IWorkFlowDto = {
        id: value.id,
        name: value.name,
        displayName: value.displayName,
        description: value.description,
        json,
        xml: value.xml,
        kProject_Id: this.state.project.id
      };
      return result;
    }
    console.error("Process data is wrong");
  };

  /**
   * Drop this workflow's tab-switch cache entry — call it on EVERY path that persists.
   *
   * The cache decides "unchanged" by comparing the server's audit UpdatedDate against the
   * token the entry was loaded with. That is sound against a cache entry being newer than
   * its token (the token is read BEFORE the document), but it is not sound against OUR OWN
   * write: a save that lands inside the same timestamp tick as the load leaves the token
   * equal while the document has moved, and the next re-select then serves the pre-save
   * copy. That window is small for a human editing a diagram and wide open for a DSL
   * script, which loads and writes a workflow many times in one turn.
   *
   * Dropping the entry costs one re-fetch on the next re-select and removes the class
   * entirely — a cache must never be able to answer for a write it did not see.
   */
  private invalidateWfCache = (workflowId: string | undefined): void => {
    if (typeof workflowId !== "string" || workflowId.length === 0) return;
    this.wfCache.delete(workflowId);
  };

  saveWorkFlow = async (wf: IWorkFlow, changeReason?: ESdChangeReason) => {
    var result = false;
    if (wf === undefined) {
      return result;
    }

    // Before any branch below: every one of them can persist, and an early return must not
    // be able to leave a stale entry behind.
    this.invalidateWfCache(wf.id);

    this.quickSyncBeforeSave();

    const { selectedWf } = this.state;
    if (selectedWf && wf.id === selectedWf.id) {
      const xml = (await this.getXmlFromModeler()) as string | undefined;

      if (wf && xml) {
        wf.xml = xml;
      }
    }

    // Collab funnel: with a live session on THIS workflow the document flows exclusively
    // through granular ops — extract→diff→queue instead of the whole-blob PUT, and the
    // wfhash dedupe never runs (the server persists from its authoritative state).
    const workflowCollab = this.partial.workflowCollabProvider;
    if (
      selectedWf &&
      wf.id === selectedWf.id &&
      workflowCollab.isWorkflowSessionActive() &&
      !workflowCollab.isApplyingRemoteWorkflowChange()
    ) {
      workflowCollab.commitWorkflowLocalDoc();
      const isExplicitSave = changeReason === undefined || changeReason === ESdChangeReason.SaveWorkflow;
      if (isExplicitSave) {
        // Ctrl+S / explicit save: everything queued goes on the wire before we declare
        // "saved", plus the save metadata (name) — mirrors collab-provider.saveThroughCollab.
        await workflowCollab.saveWorkflowThroughCollab(wf.name);
      }
      const { workFlowList } = this.state;
      const workFlowIndex = workFlowList.findIndex((listWf) => listWf.id === wf.id);
      if (workFlowIndex !== undefined && workFlowIndex > -1) {
        workFlowList[workFlowIndex] = wf;
      }
      if (!changeReason) {
        changeReason = ESdChangeReason.SaveWorkflow;
      }
      this.setState({ workFlowList, changeReason });
      return true;
    }

    let workFlowDto = this.convertWFToWfDto(wf);
    let needRefresh = true;
    if (workFlowDto) {
      if (workFlowDto.id !== undefined && workFlowDto.id.length > 0) {
        const wfCurrentHash = ObjectHash(workFlowDto);
        const wfLastSavedHash = sessionStorage.getItem(`wfhash_${workFlowDto.id}`);
        if (wfCurrentHash !== wfLastSavedHash) {
          // Stamp the hash only AFTER a successful save: stamping first meant a failed
          // PUT (e.g. 409 RowVersion conflict) left a poisoned hash that silently
          // suppressed every subsequent save of the same content.
          const res = await WorkFlowService.Update(workFlowDto.id, workFlowDto);
          if (res && res.status === 200) {
            sessionStorage.setItem(`wfhash_${workFlowDto.id}`, wfCurrentHash);
            workFlowDto = res.data;
          } else {
            if (getWorkflowFailedSaveHttpStatus(res) === 409) {
              KMainFunctions.displayWarningNotification(
                `Process: ${wf.name} was modified in another session. Reload the process to get the latest version before saving again.`,
                { duration: 0 }
              );
            }
            workFlowDto = undefined;
          }
        }
      } else {
        workFlowDto = await (await WorkFlowService.Create(workFlowDto)).data;
      }
      if (workFlowDto !== undefined) {
        result = true;
        const { workFlowList } = this.state;
        const workFlowIndex = workFlowList.findIndex((wf) => wf.id === workFlowDto?.id);
        if (workFlowIndex !== undefined && workFlowIndex > -1 && wf) {
          workFlowList[workFlowIndex] = wf;
          needRefresh = false;
          if (!changeReason) {
            changeReason = ESdChangeReason.SaveWorkflow;
          }
          this.setState({ workFlowList, changeReason });
        }
      }
    }
    if (needRefresh === true) {
      if (!changeReason) {
        changeReason = ESdChangeReason.SaveWorkflow;
      }
      this.setState({ changeReason });
    }
    return result;
  };

  async getXmlFromModeler() {
    // VTO: Fix for Process Name on XML
    const modeler = this.getModeler();
    const definitions = modeler?.getDefinitions();
    // The modeler may not be mounted yet (canvas not ready), in which case
    // getDefinitions() returns undefined — guard against reading rootElements
    // off undefined (KOA-17362).
    const process = definitions?.rootElements?.find((e) => e.$type === "bpmn:Process");
    if (process) {
      process.name = this.state.selectedWf?.name;
    }

    return this.bpmnService.exportXml();
  }

  setIsWorkFlowWindowReload = (value: boolean = true) => {
    this.setState({ isWorkFlowWindowReload: value });
  };

  setCreatingWfActive = (val: boolean) => {
    this.clearWfNodeSelection();
    this.setState({ isCreateWfActive: val });
  };

  addActionToWorkflowStep = (action: IAction, changeReason?: ESdChangeReason) => {
    const wf = this.state.selectedWf;
    const selectedNodeId = this.state.selectedWfNode?.id;
    const step = this.findStepRecursively(wf!, (s) => s.id === selectedNodeId!);
    if (wf && action && step) {
      if (!step.actions || step.actions?.length === 0) step.actions = [];
      const currentActionIndex = step?.actions.findIndex((a) => a.id == action.id);
      if (currentActionIndex > -1) {
        step.actions[currentActionIndex] = action;
      } else {
        step.actions?.push(action);
      }

      if (changeReason === undefined) changeReason = ESdChangeReason.WFStepMappingChange;
      this.saveWorkFlow(wf, changeReason);
    }
  };

  deleteActionFromWorkflowStep = (action: IAction, changeReason?: ESdChangeReason) => {
    const wf = this.state.selectedWf;
    const selectedNodeId = this.state.selectedWfNode?.id;
    const step = this.findStepRecursively(wf!, (s) => s.id === selectedNodeId!);
    if (wf && action && step) {
      if (!step.actions) step.actions = [];
      const currentActionIndex = step?.actions.findIndex((a) => a.id == action.id);
      if (currentActionIndex > -1) {
        step.actions.splice(currentActionIndex, 1);
      }

      if (changeReason === undefined) changeReason = ESdChangeReason.WFStepMappingChange;
      this.saveWorkFlow(wf, changeReason);
    }
  };

  setWFStepParameters = (step: IWFStep, params: IWFParameter[], paramType: EWFParamType, process?: IWorkFlow) => {
    const wf = process ?? this.state.selectedWf;
    if (wf) {
      this.SetStepParamsAndMappings(wf, step, params, paramType);
      this.saveWorkFlow(wf);
      this.setState({ changeReason: ESdChangeReason.WFStepMappingChange });
    }
  };

  private SetStepParamsAndMappings(wf: IWorkFlow, step: IWFStep, params: IWFParameter[], stepParamType: EWFParamType) {
    if (stepParamType == EWFParamType.Input) return;
    if (wf && step) {
      const paramsToProcess = step.outputParameters || [];
      let updatedParams = [] as IStepParameter[];
      params.forEach((param) => {
        const stepParameterCurrentState = paramsToProcess.find((p) => p.id == param.id);
        if (!stepParameterCurrentState) {
          let stepParam = {
            id: param.id,
            value: {
              category: ESymbolCategory.fixed,
              label: "Fixed Value"
            } as ISymbol
          } as IStepParameter;
          updatedParams.push(stepParam);
        } else {
          updatedParams.push(stepParameterCurrentState);
        }
      });
      step.outputParameters = updatedParams;
    }
  }

  setSelectedWfStep = (stepId: string) => {
    const step = this.findStepRecursively(this.state.selectedWf!, (s) => s.id === stepId);
    this.setState({ selectedWfNode: step });
  };

  getSelectedWfStep = () => {
    const { selectedWfNode } = this.state;
    if (!selectedWfNode) return undefined;
    const step = this.findStepRecursively(this.state.selectedWf!, (s) => s.id === selectedWfNode.id);
    return step;
  };

  updateWfStep = async (step: IWFStep, process?: IWorkFlow) => {
    const selectedWf = process || this.state.selectedWf;
    if (!selectedWf || !selectedWf.nodes) return;

    const currentStepIndex = selectedWf.nodes.steps.findIndex((s) => s.id === step.id);
    if (currentStepIndex !== undefined && currentStepIndex > -1) {
      selectedWf.nodes.steps[currentStepIndex] = step;
    }

    this.saveWorkFlow(selectedWf);
  };

  getGatewayNodeType(type: any): EWFGatewayType {
    if (type === "bpmn:ExclusiveGateway") {
      return EWFGatewayType.Conditional;
    }
    if (type === "bpmn:InclusiveGateway") {
      return EWFGatewayType.Inclusive;
    }
    return EWFGatewayType.Paralel;
  }

  setSelectedWfNodeName = (name: string) => {
    this.setState({ selectedWfNodeName: name });
  };

  getAssignmentTypeValue = (assignmentType: EWFAssignmentType | undefined) => {
    if (assignmentType) {
      switch (assignmentType) {
        case EWFAssignmentType.none:
          return "None";
        case EWFAssignmentType.everyone:
          return "Everyone";
        case EWFAssignmentType.role:
          return "Role";
        case EWFAssignmentType.dynamic:
          return "Dynamic";
        case EWFAssignmentType.external:
          return "External";
        default:
          return "Not Implemented Assignment Type";
      }
    }
    return "Assignment Type is undefined";
  };

  getProjectsWithWorkflowData = async () => {
    try {
      const response = await WorkFlowService.GetProjectList();

      if (response.status !== 200) return;

      const projects: IProjectsWithWFResponseDto[] = response.data;

      projects.forEach((project) => {
        const wfList: IWorkFlow[] = [];
        project.workflows.forEach((wf) => {
          const obj = this.convertWFDtoToWf(wf);
          wfList.push(obj);
        });
        project.workflows = wfList;
      });
      this.setState({ projectsWithWFData: projects });
      return projects;
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    }
  };

  /** In-flight dedupe: prepareModeller fires this on every modeler (re)build — overlapping builds share one request. */
  private externalWfFetch: Promise<IProjectsWithExternalWFResponseDto[] | undefined> | null = null;

  getProjectsWithExternalWorkflowData = async (): Promise<IProjectsWithExternalWFResponseDto[] | undefined> => {
    if (this.externalWfFetch) return this.externalWfFetch;
    const fetchAttempt: Promise<IProjectsWithExternalWFResponseDto[] | undefined> = (async () => {
      try {
        const response = await WorkFlowService.GetProjectsWithExternalWFScreen();

        if (response.status !== 200) return undefined;

        const projects: IProjectsWithExternalWFResponseDto[] = response.data;

        this.setState({ projectsWithExternalWFData: projects });
        return projects;
      } catch (e) {
        KMainFunctions.exceptionHandler(e);
        return undefined;
      }
    })();
    this.externalWfFetch = fetchAttempt;
    try {
      return await fetchAttempt;
    } finally {
      this.externalWfFetch = null;
    }
  };

  UpdateLock = async (workflowId: string) => {
    try {
      await WorkFlowService.UpdateLock(workflowId);
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  _getDefaultFixedValue = (dataType: string) => {
    switch (dataType) {
      case "Unknown":
        return "";
      case "Boolean":
        return false;
      case "DateTimeOffset":
        return new Date().toISOString();
      case "Guid":
        return "";
      case "Int16":
      case "Int32":
      case "Int64":
      case "Single":
      case "Decimal":
      case "Double":
        return 0;
      case "String":
        return "";
      default:
        return "";
    }
  };

  private updateSubprocessAssignments(subprocessId: string, assignment: IWFNodeAssignment): void {
    const workflow = this.state.selectedWf;
    if (!workflow) return;

    const subprocessStep = this.findStepRecursively(workflow, (s) => s.id === subprocessId);
    if (!subprocessStep?.subprocess?.steps) return;

    const updateStepAssignments = (steps: IWFStep[]) => {
      steps.forEach((step) => {
        if (step.stepType === EWFStepType.ScreenInteraction && step.useLaneAssignment) {
          step.assignment = { ...assignment };
        } else if (step.stepType === EWFStepType.Subprocess && step.subprocess?.steps) {
          updateStepAssignments(step.subprocess.steps);
        }
      });
    };

    updateStepAssignments(subprocessStep.subprocess.steps);
  }

  elementsOverlap = (el1: any, el2: any): boolean => {
    return this.syncService.elementsOverlap(el1, el2);
  };

  setSelectedWfNodeId = (nodeId: string | undefined) => {
    const { selectedWf } = this.state;
    if (selectedWf) {
      const selectedNode = this.findSelectedNode(nodeId);
      if (selectedNode) {
        this.setState({
          selectedWfNode: selectedNode
        });
      }
    }
  };

  setSelectedWfNode = (node: any | undefined) => {
    this.setState({ selectedWfNode: node });
  };

  createModeler = async (modelerRef: HTMLElement | null, isReadOnly: boolean): Promise<Modeler | NavigatedViewer> => {
    if (isReadOnly) {
      return this.bpmnService.createViewer(modelerRef);
    }
    return this.bpmnService.createModeler(modelerRef, this.partial);
  };

  getModeler = (): Modeler | NavigatedViewer | undefined => {
    return this.bpmnService.getModeler();
  };

  /** The legacy element↔side-car sync engine — the workflow collab remote applier reuses it. */
  getSyncService = (): WorkflowSyncService => {
    return this.syncService;
  };

  resetModeler = (): void => {
    this.bpmnService.resetModeler();
  };

  iterateAllStepsRecursively = (steps: IWFStep[], callback: (step: IWFStep) => void) => {
    steps.forEach((step) => {
      // Execute callback for current step
      callback(step);

      // If this is a subprocess step, recursively iterate over its steps
      if (step.stepType === EWFStepType.Subprocess && step.subprocess?.steps) {
        this.iterateAllStepsRecursively(step.subprocess.steps, callback);
      }
    });
  };

  findStepRecursively = (workflow: IWorkFlow, condition: (step: IWFStep) => boolean): IWFStep | undefined => {
    if (!workflow?.nodes?.steps?.length) return undefined;
    let foundStep: IWFStep | undefined;

    this.iterateAllStepsRecursively(workflow.nodes.steps, (step) => {
      if (condition(step) && !foundStep) {
        foundStep = step;
      }
    });

    return foundStep;
  };

  findAllStepsRecursively = (workflow: IWorkFlow, condition: (step: IWFStep) => boolean): IWFStep[] => {
    if (!workflow?.nodes?.steps?.length) return [];
    const matchingSteps: IWFStep[] = [];

    this.iterateAllStepsRecursively(workflow.nodes.steps, (step) => {
      if (condition(step)) {
        matchingSteps.push(step);
      }
    });

    return matchingSteps;
  };

  findNodeRecursively = (nodeId: string, nodeType: EWFNodeType, workflow: IWorkFlow): IWFNode | undefined => {
    if (!workflow?.nodes) return undefined;

    const searchInWorkflow = (nodes: IWFNodes): IWFNode | undefined => {
      switch (nodeType) {
        case EWFNodeType.Step:
          return nodes.steps?.find((s) => s.id === nodeId);
        case EWFNodeType.Gateway:
          return nodes.gateways?.find((g) => g.id === nodeId);
        case EWFNodeType.Event:
          // First check regular events in the events array
          const regularEvent = nodes.events?.find((e) => e.id === nodeId);
          if (regularEvent) return regularEvent;
          if (nodes.steps) {
            for (const step of nodes.steps) {
              if (step.boundaryEvent && step.boundaryEvent.id === nodeId) {
                return step.boundaryEvent;
              }
            }
          }
          return undefined;
        case EWFNodeType.Lane:
          return nodes.lanes?.find((l) => l.id === nodeId);
        case EWFNodeType.Arrow:
          // For arrows, check both forwards and decisions
          const forward = nodes.forwards?.find((f) => f.id === nodeId);
          if (forward) return forward;
          return nodes.decisions?.find((d) => d.id === nodeId);
        default:
          return undefined;
      }
    };

    const foundNode = searchInWorkflow(workflow.nodes);
    if (foundNode) return foundNode;

    const searchInSubprocesses = (steps: IWFStep[]): IWFNode | undefined => {
      for (const step of steps) {
        if (step.subprocess) {
          const found = searchInWorkflow(step.subprocess);
          if (found) return found;

          // Recursively search deeper in subprocesses
          const deeperFound = searchInSubprocesses(step.subprocess.steps);
          if (deeperFound) return deeperFound;
        }
      }
      return undefined;
    };

    return searchInSubprocesses(workflow.nodes.steps || []);
  };

  findAllNodesRecursively = (
    workflow: IWorkFlow,
    nodeType: EWFNodeType,
    condition?: (node: IWFNode) => boolean
  ): IWFNode[] => {
    const allNodes: IWFNode[] = [];

    const searchInWorkflow = (nodes: IWFNodes) => {
      let nodeArray: IWFNode[] = [];

      switch (nodeType) {
        case EWFNodeType.Step:
          nodeArray = nodes.steps || [];
          break;
        case EWFNodeType.Gateway:
          nodeArray = nodes.gateways || [];
          break;
        case EWFNodeType.Event:
          // Include both regular events and boundary events
          const regularEvents = nodes.events || [];
          const boundaryEvents: IWFNode[] = [];

          // Collect boundary events from step.boundaryEvent properties
          if (nodes.steps) {
            for (const step of nodes.steps) {
              if (step.boundaryEvent) {
                boundaryEvents.push(step.boundaryEvent);
              }
            }
          }

          nodeArray = [...regularEvents, ...boundaryEvents];
          break;
        case EWFNodeType.Lane:
          nodeArray = nodes.lanes || [];
          break;
        case EWFNodeType.Arrow:
          // For arrows, include both forwards and decisions
          nodeArray = [...(nodes.forwards || []), ...(nodes.decisions || [])];
          break;
        default:
          return;
      }

      const filteredNodes = condition ? nodeArray.filter(condition) : nodeArray;
      allNodes.push(...filteredNodes);
    };

    if (workflow?.nodes) {
      searchInWorkflow(workflow.nodes);
    }

    const searchInSubprocesses = (steps: IWFStep[]) => {
      for (const step of steps) {
        if (step.subprocess) {
          searchInWorkflow(step.subprocess);
          searchInSubprocesses(step.subprocess.steps);
        }
      }
    };

    if (workflow?.nodes?.steps) {
      searchInSubprocesses(workflow.nodes.steps);
    }

    return allNodes;
  };

  private findSelectedNode(nodeId: string | undefined): IWFNode | undefined {
    const selectedId = nodeId ?? this.state.selectedWfNode?.id;
    if (!selectedId || !this.state.selectedWf) return undefined;

    const bpmnElement = this.bpmnService.getElementById(selectedId);
    if (bpmnElement) {
      const nodeType = this.syncService.getNodeType(bpmnElement);
      if (nodeType) {
        const foundNode = this.findNodeRecursively(selectedId, nodeType, this.state.selectedWf);
        if (foundNode) {
          return foundNode;
        }
      }
    }

    // Fallback: try to find the node by trying all possible node types
    const nodeTypes = [EWFNodeType.Step, EWFNodeType.Gateway, EWFNodeType.Event, EWFNodeType.Lane, EWFNodeType.Arrow];

    for (const nodeType of nodeTypes) {
      const foundNode = this.findNodeRecursively(selectedId, nodeType, this.state.selectedWf);
      if (foundNode) {
        return foundNode;
      }
    }

    return undefined;
  }

  /**
   * Reconciles the workflow JSON model with the BPMN diagram — BOTH directions.
   *
   * The diagram is the authority: a node the canvas no longer has is removed from the
   * model, and a node the canvas HAS but the model lacks is adopted into it.
   *
   * KOA-18136 — this used to be SUBTRACTIVE ONLY, and the missing half was the root of
   * the "Step not found." family. Nodes enter the model exclusively through the
   * `processPendingChanges` drain (`shape.create` → `syncElement`), which is a
   * best-effort side channel: it reads `this.state.selectedWf` (a React snapshot that a
   * concurrent script can replace mid-drain) and returns early when that snapshot is not
   * yet committed. Whatever it missed was lost PERMANENTLY, because the only other
   * model↔diagram pass could delete but never add. The live evidence: a build whose
   * canvas carried both user tasks persisted `nodes.steps` with only one of them — while
   * `nodes.forwards` still held the flows referencing the missing node, i.e. a model that
   * was not merely stale but internally inconsistent.
   *
   * Everything that resolves a node against the MODEL then failed on a node the author
   * could see on the canvas — `MAP_INPUT`, `PHASE_ASSIGN`, and the backend's own
   * `wfJson.Nodes.Steps.FindNested(...)`, which is why one user task shipped without its
   * managed screen. Since `saveWorkFlow` calls `quickSyncBeforeSave` on every persist,
   * closing the loop here means a saved workflow can no longer be a strict subset of the
   * canvas the user is looking at.
   */
  syncWorkflowWithDiagram = () => {
    this.reconcileWorkflowWithDiagram("reconcile", true);
  };

  /**
   * KOA-18136 §37 — ADOPT-ONLY reconcile: the variant a LOOKUP is allowed to run.
   *
   * Resolving a node against the model used to run the full reconcile, subtractive half
   * included, so a lookup could DELETE the very step it was looking for. That is not a
   * theoretical ordering hazard: a script whose canvas died mid-run kept looking steps
   * up, each lookup read an empty canvas, and the model was emptied one lookup at a
   * time while the author's commands failed "Step not found." (live session be14b8f5 —
   * 8 of 14 created steps survived).
   *
   * Removal is a SAVE-time decision, made once, against a canvas that was actually
   * read. A lookup only ever needs the additive direction: bring in what the canvas has
   * and the model lacks.
   */
  adoptDiagramNodesIntoWorkflow = () => {
    this.reconcileWorkflowWithDiagram("adopt", false);
  };

  /**
   * The one place model↔diagram reconciliation happens, in both variants.
   *
   * `mayRemove` is the authority to run the subtractive half — and it is granted only
   * when BOTH the caller asked for it and the canvas read is trustworthy
   * (`classifyCanvasRead`). An unreadable read is recorded and otherwise does nothing:
   * "I cannot see the canvas" must never be executed as "the canvas is empty".
   */
  private reconcileWorkflowWithDiagram(purpose: string, mayRemove: boolean): void {
    const workflow = this.state.selectedWf;
    if (!workflow) return;

    // KOA-18136 §32 — an EMPTY model is the case this sync exists for, not a
    // reason to skip it.
    //
    // `!workflow.nodes` used to return here, and that is exactly the state a
    // freshly AI-built process is in: the diagram carries the whole graph while
    // the model side-car has never been populated. The only code that ever
    // populated it from the canvas is bound to the KEYBOARD (Ctrl+S / Ctrl+Z /
    // Ctrl+Y / Delete / Ctrl+V in the process window), so a process built without
    // a human touching the keyboard was saved with no nodes at all — the right
    // panel showed no properties for any node, and the backend reported
    // "'Start' / 'End' / 'Step' node does not exist in process" (live 2026-08-16,
    // session d199aa01). Seeding the skeleton lets the adoption half below fill
    // it from the diagram, which is what it was written to do.
    if (!workflow.nodes) {
      workflow.nodes = emptyWorkflowNodes();
    }

    const modelerPresent: boolean = !!this.bpmnService.getModeler();
    const diagramElements: ModdleElement[] = modelerPresent ? this.bpmnService.getAllElements() : [];
    const readability: CanvasReadability = classifyCanvasRead({
      modelerPresent,
      elementCount: diagramElements.length
    });

    if (readability !== "readable") {
      // Blind. Record it on the channel the tool response reads, and change NOTHING —
      // not even the additive half, which has nothing to add from an empty read.
      recordCanvasIncident({
        purpose,
        cause: readability,
        detail: describeCanvasReadability(readability),
        windowModelerPresent: isBpmnModelerReady(),
        elementCount: diagramElements.length
      });
      return;
    }
    noteCanvasReadable(diagramElements.length);

    const diagramElementIds = new Set(diagramElements.map((el) => el.id));

    if (mayRemove) {
      this.syncWorkflowNodes(workflow.nodes, diagramElementIds);
    }
    this.adoptDiagramOnlyNodes(workflow, diagramElements);
  }

  /**
   * Adopt every diagram element the model does not carry (the additive half of
   * `syncWorkflowWithDiagram`).
   *
   * WHICH elements, and in what order, is decided by the pure
   * `selectDiagramNodesToAdopt` (see that module for why the order is a contract).
   * `determineTargetWorkflow` is the same resolver `processPendingChanges` uses, so an
   * element inside a subprocess is adopted into that subprocess, not the root.
   */
  private adoptDiagramOnlyNodes(workflow: IWorkFlow, diagramElements: ModdleElement[]) {
    const toAdopt: IDiagramElementLike[] = selectDiagramNodesToAdopt(workflow.nodes, diagramElements);
    for (const element of toAdopt) {
      const targetWorkflow = this.determineTargetWorkflow(element, workflow);
      this.syncService.syncElement(element as ModdleElement, workflow, targetWorkflow);
      console.log(
        `[WorkflowProvider] Adopting ${element.businessObject?.$type} ${element.id} - ` +
          `present in diagram, missing from model`
      );
    }
  }

  /**
   * Recursively syncs workflow nodes, removing any that don't exist in the diagram
   */
  private syncWorkflowNodes(nodes: IWFNodes, diagramElementIds: Set<string>) {
    if (!nodes) return;

    if (nodes.steps) {
      nodes.steps = nodes.steps.filter((step) => {
        const exists = diagramElementIds.has(step.id);
        if (!exists) {
          console.log(`[WorkflowProvider] Removing step ${step.id} (${step.name}) - not found in diagram`);
        }
        return exists;
      });

      nodes.steps.forEach((step) => {
        // Symmetry with the XML safety net (BpmnService.pruneShapelessBoundaryEvents): a step
        // may survive while its attached boundary event no longer has a rendered shape. Clear
        // the stale reference so the JSON never points at an element that isn't on the diagram.
        if (step.boundaryEvent?.id && !diagramElementIds.has(step.boundaryEvent.id)) {
          console.log(
            `[WorkflowProvider] Clearing boundary event ${step.boundaryEvent.id} on step ${step.id} - not found in diagram`
          );
          step.boundaryEvent = undefined;
        }
        if (step.subprocess) {
          this.syncWorkflowNodes(step.subprocess, diagramElementIds);
        }
      });
    }

    if (nodes.gateways) {
      nodes.gateways = nodes.gateways.filter((gateway) => {
        const exists = diagramElementIds.has(gateway.id);
        if (!exists) {
          console.log(`[WorkflowProvider] Removing gateway ${gateway.id} (${gateway.name}) - not found in diagram`);
        }
        return exists;
      });
    }

    if (nodes.events) {
      nodes.events = nodes.events.filter((event) => {
        const exists = diagramElementIds.has(event.id);
        if (!exists) {
          console.log(`[WorkflowProvider] Removing event ${event.id} (${event.name}) - not found in diagram`);
        }
        return exists;
      });
    }

    if (nodes.lanes) {
      nodes.lanes = nodes.lanes.filter((lane) => {
        const exists = diagramElementIds.has(lane.id);
        if (!exists) {
          console.log(`[WorkflowProvider] Removing lane ${lane.id} (${lane.name}) - not found in diagram`);
        }
        return exists;
      });
    }

    if (nodes.forwards) {
      nodes.forwards = nodes.forwards.filter((forward) => {
        const exists = diagramElementIds.has(forward.id);
        if (!exists) {
          console.log(`[WorkflowProvider] Removing forward ${forward.id} - not found in diagram`);
        }
        return exists;
      });
    }

    if (nodes.decisions) {
      nodes.decisions = nodes.decisions.filter((decision) => {
        const exists = diagramElementIds.has(decision.id);
        if (!exists) {
          console.log(`[WorkflowProvider] Removing decision ${decision.id} - not found in diagram`);
        }
        return exists;
      });
    }
  }

  quickSyncBeforeSave = () => {
    console.log("[WorkflowProvider] Running quick sync before save...");
    this.syncWorkflowWithDiagram();
  };

  manualSyncWorkflow = () => {
    console.log("[WorkflowProvider] Manual sync triggered...");
    this.syncWorkflowWithDiagram();

    const workflow = this.state.selectedWf;
    if (workflow) {
      this.saveWorkFlow(workflow, ESdChangeReason.WorkflowListUpdate);
    }
  };
}
